import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import client, { formatApiError } from "@/lib/api";

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

const TENANTS_FALLBACK = {
  fisbo: { slug: "fisbo", name: "FISBO Compass", tagline: "Fall in love with your numbers.", logo_icon: "compass", primary: "#2E5C41", primary_hover: "#244A34", primary_light: "#EAF0EC" },
};

// Resolve which partner to brand for: subdomain first, then ?cu= link, else default.
function preferredSlug() {
  const host = window.location.hostname;
  const parts = host.split(".");
  const IGNORE = new Set(["www", "app", "mail", "localhost", "numbers-roadmap"]);
  let sub = null;
  if (parts.length >= 3 && !IGNORE.has(parts[0])) sub = parts[0];
  const cu = new URLSearchParams(window.location.search).get("cu");
  return sub || cu || "fisbo";
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = logged out
  const [tenant, setTenant] = useState(TENANTS_FALLBACK.fisbo);

  const applyTenant = useCallback((t) => {
    if (!t) return;
    setTenant(t);
    const root = document.documentElement;
    root.style.setProperty("--tenant-primary", t.primary);
    root.style.setProperty("--tenant-primary-hover", t.primary_hover);
    root.style.setProperty("--tenant-primary-light", t.primary_light);
  }, []);

  const loadTenant = useCallback(async (slug) => {
    try {
      const { data } = await client.get(`/tenants/${slug}`);
      applyTenant(data);
    } catch {
      applyTenant(TENANTS_FALLBACK.fisbo);
    }
  }, [applyTenant]);

  useEffect(() => {
    const token = localStorage.getItem("fisbo_token");
    if (!token) {
      setUser(false);
      loadTenant(preferredSlug());
      return;
    }
    client.get("/auth/me")
      .then(({ data }) => { setUser(data); loadTenant(data.tenant_slug || "fisbo"); })
      .catch(() => { localStorage.removeItem("fisbo_token"); setUser(false); loadTenant(preferredSlug()); });
  }, [loadTenant]);

  const login = async (email, password) => {
    const { data } = await client.post("/auth/login", { email, password });
    localStorage.setItem("fisbo_token", data.token);
    setUser(data.user);
    loadTenant(data.user.tenant_slug);
    return data.user;
  };

  const register = async (payload) => {
    const { data } = await client.post("/auth/register", payload);
    localStorage.setItem("fisbo_token", data.token);
    setUser(data.user);
    loadTenant(data.user.tenant_slug);
    return data.user;
  };

  const logout = () => {
    localStorage.removeItem("fisbo_token");
    localStorage.removeItem("fisbo_pending_diagnostic");
    setUser(false);
    window.location.replace("/");
  };

  const refreshUser = async () => {
    try {
      const { data } = await client.get("/auth/me");
      setUser(data);
    } catch { /* ignore */ }
  };

  return (
    <AuthContext.Provider value={{ user, tenant, login, register, logout, loadTenant, refreshUser, formatApiError }}>
      {children}
    </AuthContext.Provider>
  );
}
